import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { Person, Roles } from "../../services/Person Management";
import personAdapter from "../../adapters/person";
import { select as select_document_types } from "./document_type";
import { select as select_fiscal_conditions } from "./fiscal_condition";
import { select as select_characteristic_types } from "./characteristic_type";

const service = new Person();
const rolesService = new Roles();
const initialState = {
  grid: { page: 1, skip: 0, take: 6, rows: 0, data: [], filters: { active: undefined, search: "", roleIds: [] } },
  roles: { data: [], loading: false, error: "" }, gridRequestId: "", loading: false, formIsReady: false,
  data: "", message: "", error: "", select: [],
};

export const grid = createAsyncThunk("PERSON/grid", async ({ skip = 0, take = 6, currentPage = 1, filters = {} }) => {
  const normalized = { active: typeof filters.active === "boolean" ? filters.active : undefined, search: filters.search || "", roleIds: filters.roleIds || [] };
  const { data } = await service.grid({ skip, take, ...normalized });
  return { skip, take, page: currentPage, filters: normalized, gridData: data };
});
export const availableRoles = createAsyncThunk("PERSON/availableRoles", async () => (await rolesService.available()).data);
export const getById = createAsyncThunk("PERSON/getById", async (id) => ({ person: (await service.getById(id)).data }));
export const select = createAsyncThunk("PERSON/select", async (options) => {
  const { data: people } = await service.select(options);
  return { people: [{ value: "", label: "Seleccione una opción." }, ...people.map((item) => ({ value: item.id, label: item.full_name }))] };
});
export const updateRoles = createAsyncThunk("PERSON/updateRoles", async ({ id, roleIds }) => {
  await service.updateRoles(id, roleIds);
});
export const changeActive = createAsyncThunk("PERSON/changeActive", async ({ id }, { dispatch, getState }) => {
  await service.changeActive(id);
  const current = getState().person.grid;
  dispatch(grid({ skip: current.skip, take: current.take, currentPage: current.page, filters: current.filters }));
});
export const form = createAsyncThunk("PERSON/form", async (id, { dispatch }) => {
  const requests = [
    dispatch(select_document_types()),
    dispatch(select_fiscal_conditions()),
    dispatch(select_characteristic_types({ base_url: service.base_url })),
    dispatch(availableRoles()),
  ];
  if (id) requests.push(dispatch(getById(id)));
  await Promise.all(requests);
});
export const submit = createAsyncThunk("PERSON/submit", async ({ id, data }, { rejectWithValue }) => {
  try {
    const payload = personAdapter.from_form_to_api(data);
    if (id) await service.update(id, payload); else await service.create(payload);
    return { message: `Persona ${id ? "modificada" : "creada"} correctamente.` };
  } catch (error) { return rejectWithValue(error.errors || error.message); }
});

const personSlice = createSlice({
  name: "PERSON", initialState, reducers: { clean: () => initialState },
  extraReducers: (builder) => builder
    .addCase(grid.pending, (state, action) => { state.gridRequestId = action.meta.requestId; state.loading = true; state.error = ""; })
    .addCase(grid.fulfilled, (state, action) => {
      if (state.gridRequestId !== action.meta.requestId) return;
      const { page, skip, take, filters, gridData } = action.payload;
      state.grid = { ...state.grid, page, skip, take, filters, rows: gridData.rows, data: personAdapter.from_api_to_grid(gridData.data) };
      state.gridRequestId = "";
      state.loading = false;
    })
    .addCase(grid.rejected, (state, action) => {
      if (state.gridRequestId !== action.meta.requestId) return;
      state.gridRequestId = "";
      state.loading = false;
      state.error = action.payload || action.error.message || "Error desconocido";
    })
    .addCase(availableRoles.fulfilled, (state, { payload }) => { state.roles.data = payload || []; state.roles.error = ""; })
    .addCase(availableRoles.rejected, (state, action) => { state.roles.error = action.error.message || "No pudimos cargar los roles."; })
    .addCase(getById.fulfilled, (state, { payload }) => { state.data = payload.person || ""; })
    .addCase(select.fulfilled, (state, { payload }) => { state.select = payload.people; })
    .addCase(submit.fulfilled, (state, { payload }) => { state.message = payload.message; state.formIsReady = true; })
    .addMatcher(isAnyOf(getById.pending, select.pending, form.pending, submit.pending, updateRoles.pending, changeActive.pending), (state) => { state.loading = true; state.error = ""; state.formIsReady = false; })
    .addMatcher(isAnyOf(getById.fulfilled, getById.rejected, select.fulfilled, select.rejected, form.fulfilled, submit.fulfilled, submit.rejected, updateRoles.fulfilled, updateRoles.rejected, changeActive.fulfilled, changeActive.rejected), (state) => { state.loading = false; })
    .addMatcher(isAnyOf(getById.rejected, select.rejected, submit.rejected, updateRoles.rejected), (state, action) => { state.error = action.payload || action.error.message || "Error desconocido"; })
    .addMatcher(isAnyOf(availableRoles.pending), (state) => { state.roles.loading = true; })
    .addMatcher(isAnyOf(availableRoles.fulfilled, availableRoles.rejected), (state) => { state.roles.loading = false; }),
});
export const { clean } = personSlice.actions;
export default personSlice.reducer;
